/**
 * ProtectedRoute Component
 * 
 * Wraps routes that require authentication and handles role-based access.
 * Supports both role-based and permission-based access control.
 * 
 * Props:
 * - requiredRole: string | string[] - Role(s) required to access
 * - requiredPermission: string - Permission required to access
 * - redirectTo: string - Where to redirect if access denied (default: show error)
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function ProtectedRoute({ 
  children, 
  requiredRole, 
  requiredPermission,
  redirectTo = null 
}) {
  const { 
    isAuthenticated, 
    loading, 
    hasRole, 
    hasPermission, 
    canAccess,
    userRole,
    user
  } = useAuth();
  const location = useLocation();

  console.log('🛡️ [ProtectedRoute] Checking access:', {
    path: location.pathname,
    requiredRole,
    requiredPermission,
    isAuthenticated,
    loading,
    userRole,
    isDeveloper: user?.isDeveloper
  });

  // Show loading state while checking auth
  if (loading) {
    console.log('🛡️ [ProtectedRoute] Still loading auth...');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    console.log('🛡️ [ProtectedRoute] ❌ Not authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Developer bypass for role checks
  const isDeveloper = user?.isDeveloper || userRole === 'developer';

  // Check role if required
  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    const hasRequiredRole = isDeveloper || roles.some(role => hasRole(role));
    
    console.log('🛡️ [ProtectedRoute] Role check:', {
      requiredRoles: roles,
      userRole,
      isDeveloper,
      hasRequiredRole
    });
    
    if (!hasRequiredRole) {
      console.log('🛡️ [ProtectedRoute] ❌ Role check failed');
      if (redirectTo) {
        return <Navigate to={redirectTo} replace />;
      }
      return <AccessDenied userRole={userRole} />;
    }
  }

  // Check permission if required
  if (requiredPermission && !isDeveloper && !hasPermission(requiredPermission)) {
    console.log('🛡️ [ProtectedRoute] ❌ Permission check failed:', requiredPermission);
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return <AccessDenied userRole={userRole} />;
  }

  // Check route access using the permissions system
  const currentPath = location.pathname;
  const routeAccess = isDeveloper || canAccess(currentPath);
  
  console.log('🛡️ [ProtectedRoute] Route access check:', {
    currentPath,
    canAccess: routeAccess,
    isDeveloper
  });
  
  if (!routeAccess) {
    console.log('🛡️ [ProtectedRoute] ❌ Route access denied');
    // Users trying to access non-allowed routes get redirected to dashboard
    if (userRole === 'user') {
      return <Navigate to="/" replace />;
    }
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    return <AccessDenied userRole={userRole} />;
  }

  console.log('🛡️ [ProtectedRoute] ✅ All checks passed, rendering children');
  return children;
}

/**
 * Access Denied Component
 * Shown when user doesn't have permission to access a page
 */
function AccessDenied({ userRole }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="text-center max-w-md px-6">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-900/30 flex items-center justify-center">
          <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-red-500 mb-2">Access Denied</h1>
        <p className="text-gray-400 mb-4">
          You don't have permission to access this page.
        </p>
        <p className="text-gray-500 text-sm">
          Your current role: <span className="text-gray-300 font-medium capitalize">{userRole}</span>
        </p>
        <a 
          href="/"
          className="inline-block mt-6 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
        >
          Return to Dashboard
        </a>
      </div>
    </div>
  );
}

/**
 * RoleRoute Component
 * Alternative syntax for role-protected routes
 */
export function RoleRoute({ roles, children, fallback = null }) {
  const { hasRole, isAuthenticated, loading } = useAuth();

  if (loading) return null;
  if (!isAuthenticated) return fallback;

  const allowedRoles = Array.isArray(roles) ? roles : [roles];
  const hasAccess = allowedRoles.some(role => hasRole(role));

  return hasAccess ? children : fallback;
}

/**
 * PermissionRoute Component
 * Route protection based on specific permissions
 */
export function PermissionRoute({ permission, children, fallback = null }) {
  const { hasPermission, isAuthenticated, loading } = useAuth();

  if (loading) return null;
  if (!isAuthenticated) return fallback;

  return hasPermission(permission) ? children : fallback;
}

